import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { wearClass } from './helpers';
import {
  MARKETPLACES, DIRECTIONS, fetchMarketHistory, marketLabel, summarize, vsReference,
  isCompleted, stateLabel,
} from './marketHistory';
import { StickerStrip } from './historyShared';
import './HistoryTab.css';
import { findSkinImage } from './skinIndex';

// History — what you actually bought and sold on a marketplace, straight from
// that marketplace rather than inferred from the tracker. The dropdown picks
// the market (CSFloat is the only one wired up); the two buttons pick which
// side of the ledger you're looking at.
//
// HistoryTabZone.jsx is this tab's Zone twin: same data, same numbers.


const SORTS = [
  { key: 'date',  label: 'Newest first' },
  { key: 'old',   label: 'Oldest first' },
  { key: 'high',  label: 'Highest value' },
  { key: 'low',   label: 'Lowest value' },
];

const money = n => Math.abs(Number(n) || 0).toFixed(2);
const dateOf = iso => new Date(iso);

export default function HistoryTab({ settings, skinsData = [] }) {
  const [market, setMarket]       = useState('csfloat');
  const [direction, setDirection] = useState('sale');
  const [query, setQuery]         = useState('');
  const [sort, setSort]           = useState('date');
  const [entries, setEntries]     = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchMarketHistory(market)
      .then(list => { if (live) { setEntries(list); setLoading(false); } })
      .catch(() => { if (live) { setEntries([]); setLoading(false); } });
    return () => { live = false; };
  }, [market]);

  const sideEntries = useMemo(
    () => entries.filter(e => e.direction === direction),
    [entries, direction]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? sideEntries.filter(e => e.skin.toLowerCase().includes(q) || (e.counterparty || '').toLowerCase().includes(q))
      : sideEntries;
    const by = {
      date: (a, b) => dateOf(b.date) - dateOf(a.date),
      old:  (a, b) => dateOf(a.date) - dateOf(b.date),
      high: (a, b) => b.price - a.price,
      low:  (a, b) => a.price - b.price,
    }[sort];
    return [...list].sort(by);
  }, [sideEntries, query, sort]);

  const totals = useMemo(() => summarize(sideEntries), [sideEntries]);
  const shown  = useMemo(() => summarize(filtered), [filtered]);

  const isSale = direction === 'sale';
  const profC  = settings?.profitTextColor || 'var(--green)';
  const lossC  = settings?.lossTextColor   || 'var(--red)';

  return (
    <div className="hst-root classic lv2-root">

      {/* ── Headline totals ────────────────────────── */}
      <div className="pt-hero">
        <div className="pt-hero-cell">
          <div className="k">{isSale ? 'Gross sales' : 'Total spent'}</div>
          <div className="v">${money(totals.gross)}</div>
          <div className="s">
            {totals.count} {isSale ? 'sale' : 'purchase'}{totals.count !== 1 ? 's' : ''} on {marketLabel(market)}
            {totals.cancelled > 0 && (
              <span className="pt-dead-note"> · {totals.cancelled} never completed</span>
            )}
          </div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">{isSale ? 'Net after fees' : 'Average price'}</div>
          <div className="v">${money(isSale ? totals.net : totals.avg)}</div>
          <div className="s">
            {isSale ? `$${money(totals.fees)} paid in marketplace fees` : 'per item bought'}
          </div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Largest {isSale ? 'sale' : 'buy'}</div>
          <div className="v">{totals.largest ? `$${money(totals.largest.price)}` : 'n/a'}</div>
          <div className="s">{totals.largest ? totals.largest.skin : 'nothing recorded yet'}</div>
        </div>
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ position: 'static', marginBottom: 16 }}>
        <div className="hst-market">
          <span className="hst-market-label">Marketplace</span>
          <select className="lv2-select" value={market} onChange={e => setMarket(e.target.value)} aria-label="Marketplace">
            {MARKETPLACES.map(m => (
              <option key={m.id} value={m.id} disabled={!m.ready}>
                {m.ready ? m.label : `${m.label} (soon)`}
              </option>
            ))}
          </select>
        </div>

        {DIRECTIONS.map(d => (
          <button key={d.key} className={`lv2-chip ${direction === d.key ? 'on' : ''}`}
            onClick={() => setDirection(d.key)}>
            {d.label}
          </button>
        ))}

        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search skin or trader…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>

        {/* Count and money must describe the same rows: the total excludes
            trades that never completed, so the count does too. */}
        <span style={{ marginLeft: 'auto', fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}>
          {shown.count} {isSale ? 'sale' : 'purchase'}{shown.count !== 1 ? 's' : ''} ·{' '}
          <span style={{ color: isSale ? profC : lossC }}>${money(shown.gross)}</span>
          {shown.cancelled > 0 && <span className="pt-dead-note"> · {shown.cancelled} cancelled</span>}
        </span>

        <select className="lv2-select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort history">
          {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {/* ── Ledger ─────────────────────────────────── */}
      {loading ? (
        <div className="lv2-empty">Loading {marketLabel(market)} history…</div>
      ) : filtered.length === 0 ? (
        <div className="lv2-empty">
          {sideEntries.length === 0
            ? `No ${isSale ? 'sales' : 'purchases'} recorded on ${marketLabel(market)} yet.`
            : 'No trades match your search.'}
        </div>
      ) : (
        <div className="hst-ledger">
          <div className="hst-head-row">
            <span>Date</span>
            <span>Item</span>
            <span>Float</span>
            <span>{isSale ? 'Buyer' : 'Seller'}</span>
            <span>vs market</span>
            <span style={{ textAlign: 'right' }}>{isSale ? 'Sold for' : 'Paid'}</span>
          </div>

          {filtered.map((e, i) => {
            const img   = findSkinImage(skinsData, e.skin, e.condition);
            const ref   = vsReference(e);
            const ts    = dateOf(e.date);
            const dead  = !isCompleted(e);
            const label = stateLabel(e);
            return (
              <motion.div key={e.id} className={`hst-row ${e.direction}${dead ? ' dead' : ''}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 18) * 0.03 }}
              >
                <div className="hst-date">
                  {ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  <span className="t">{ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                </div>

                <div className="hst-item">
                  {img ? <img src={img} alt="" loading="lazy" /> : <div className="hst-thumb-blank" />}
                  <div style={{ minWidth: 0 }}>
                    <div className="hst-name" title={e.skin}>{e.skin}</div>
                    <div className="hst-meta">
                      {label && <span className={`hst-state ${e.state}`}>{label}</span>}
                      <span className={`hst-wear ${wearClass(e.condition)}`}>{e.condition || 'n/a'}</span>
                      {e.paintSeed != null && <span>seed {e.paintSeed}</span>}
                    </div>
                  </div>
                </div>

                <div className="hst-num ink">{e.float != null ? e.float.toFixed(4) : 'n/a'}</div>

                <div className="hst-num">{e.counterparty || 'n/a'}</div>

                {ref ? (
                  <div className={`hst-ref ${ref.diff >= 0 ? 'win' : 'lose'}`}>
                    {ref.diff >= 0 ? '+' : '−'}${money(ref.diff)}
                    <span className="p">{ref.pct >= 0 ? '+' : '−'}{Math.abs(ref.pct).toFixed(1)}% vs ref</span>
                  </div>
                ) : <div className="hst-num">n/a</div>}

                <div className="hst-money">
                  <div className="v">${money(e.price)}</div>
                  <div className="s">
                    {dead
                      ? 'no money moved'
                      : isSale ? `net $${money(e.net)} after $${money(e.fee)} fee` : 'no buyer fee'}
                  </div>
                </div>

                <StickerStrip stickers={e.stickers} skinsData={skinsData} />
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
